import React, { useEffect, useState } from 'react';
import ApproachVisualizer from './ApproachVisualizer';

const V1 = '#7F00FF', V2 = '#B163FF', GREEN = '#2ECC71', ORANGE = '#E8834C', RED = '#F04155';
const LEVEL_COLOR = { Easy: GREEN, Medium: ORANGE, Advanced: RED };

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1400); });
  };
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '7px 12px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)' }}>
        <button onClick={copy} style={{
          fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
          border: `1px solid ${copied ? GREEN : 'var(--border)'}`,
          background: copied ? 'rgba(46,204,113,0.12)' : 'transparent',
          color: copied ? GREEN : 'var(--muted)', cursor: 'pointer',
        }}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      <pre style={{ margin: 0, padding: '18px 20px', overflowX: 'auto', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 'clamp(14px, 0.7vw + 12px, 15.5px)', lineHeight: 1.75, color: '#E8E1FF' }}>
        <code>{code}</code>
      </pre>
    </div>
  );
};

const VersionCard = ({ version }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 16, padding: '24px 26px', background: 'var(--card-solid)' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
      <h4 style={{ fontFamily: "'Inter',sans-serif", fontSize: 'clamp(18px, 0.9vw + 14px, 20px)', fontWeight: 800, color: 'var(--text)', margin: 0 }}>{version.name}</h4>
      <div style={{ display: 'flex', gap: 8 }}>
        <span style={pillStyle(V1)}>Time {version.complexity.time}</span>
        <span style={pillStyle(V2)}>Space {version.complexity.space}</span>
      </div>
    </div>
    <p style={{ fontSize: 'clamp(15px, 0.5vw + 13px, 16.5px)', lineHeight: 1.8, color: 'var(--text-2)', marginBottom: version.steps ? 18 : 0 }}>{version.explanation}</p>

    {version.steps && (
      <>
        <div style={{ fontSize: 'clamp(12px, 0.35vw + 10.5px, 13px)', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 11 }}>
          Step by step
        </div>
        <ol style={{ margin: 0, paddingLeft: 22, marginBottom: version.visual ? 18 : 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
          {version.steps.map((s, i) => (
            <li key={i} style={{ fontSize: 'clamp(14.5px, 0.45vw + 12.5px, 15.5px)', lineHeight: 1.7, color: 'var(--text)' }}>{s}</li>
          ))}
        </ol>
      </>
    )}

    {version.visual && <ApproachVisualizer visual={version.visual} />}

    <CodeBlock code={version.code} />
  </div>
);
const pillStyle = color => ({
  fontSize: 'clamp(12px, 0.3vw + 10.5px, 13px)', fontWeight: 800, padding: '5px 12px', borderRadius: 999,
  background: `${color}15`, color, whiteSpace: 'nowrap',
});

const DsaDrillModal = ({ drill, onClose }) => {
  // No document.body scroll-lock here on purpose — this modal is always opened
  // from inside DsaExplanationModal, which already owns the lock. Toggling it again
  // here would re-enable page scroll the moment this inner modal closes.
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!drill) return null;
  const levelColor = LEVEL_COLOR[drill.level] || V2;

  return (
    <div className="dsa-drill-overlay" onClick={onClose}>
      <div className="dsa-drill-modal" onClick={e => e.stopPropagation()}>
        <div className="dsa-drill-header">
          <div>
            <span style={{ fontSize: 'clamp(12px, 0.3vw + 10.5px, 13px)', fontWeight: 800, padding: '4px 11px', borderRadius: 999, background: `${levelColor}18`, color: levelColor }}>
              {drill.level}
            </span>
            <h2 style={{ fontFamily: "'Inter',sans-serif", fontSize: 'clamp(22px, 1.3vw + 15px, 25px)', fontWeight: 800, color: 'var(--text)', margin: '11px 0 0' }}>
              {drill.title}
            </h2>
          </div>
          <button className="dsa-drill-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="dsa-drill-body">
          {drill.crossReference ? (
            <div style={{
              padding: '20px 22px', borderRadius: 14, fontSize: 'clamp(15px, 0.5vw + 13px, 16.5px)', lineHeight: 1.8,
              background: `${V1}0c`, border: `1px solid ${V1}25`, color: 'var(--text-2)',
            }}>{drill.crossReference}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {drill.versions.map((v, i) => <VersionCard key={i} version={v} />)}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .dsa-drill-overlay {
          position: fixed; inset: 0; z-index: 9400;
          background: rgba(10,6,20,0.72); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; padding: 116px 24px 24px;
          animation: dsaDrillFade 0.2s ease;
        }
        .dsa-drill-modal {
          width: min(920px, 96vw); height: min(84vh, 860px);
          background: var(--card-solid); border: 1px solid var(--border); border-radius: 24px;
          display: flex; flex-direction: column; overflow: hidden;
          box-shadow: 0 30px 90px -12px rgba(0,0,0,0.5);
          animation: dsaDrillPop 0.25s cubic-bezier(0.2,1,0.3,1);
        }
        .dsa-drill-header {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
          padding: 26px 30px 20px; border-bottom: 1px solid var(--border); flex-shrink: 0;
        }
        .dsa-drill-close {
          flex-shrink: 0; width: 36px; height: 36px; border-radius: 50%;
          border: 1px solid var(--border); background: var(--bg3); color: var(--muted);
          font-size: 13.5px; cursor: pointer; display: flex; align-items: center; justify-content: center;
          transition: background 0.2s;
        }
        .dsa-drill-close:hover { background: var(--bg2); color: var(--text); }
        .dsa-drill-body {
          flex: 1; overflow-y: auto; padding: 26px 30px 34px; min-height: 0;
          scroll-behavior: smooth; overscroll-behavior: contain; -webkit-overflow-scrolling: touch;
        }

        @keyframes dsaDrillFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dsaDrillPop { from { opacity: 0; transform: scale(0.96) translateY(12px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) {
          .dsa-drill-overlay, .dsa-drill-modal { animation: none !important; }
        }
        @media (max-width: 980px) {
          .dsa-drill-modal { width: 94vw; }
        }
        @media (max-width: 640px) {
          .dsa-drill-overlay { padding: 84px 10px 10px; }
          .dsa-drill-modal { width: 98vw; height: 82vh; border-radius: 16px; }
          .dsa-drill-header { padding: 20px 18px 16px; }
          .dsa-drill-body { padding: 20px 18px 26px; }
        }
        @media (max-width: 420px) {
          .dsa-drill-overlay { padding: 78px 6px 6px; }
          .dsa-drill-modal { height: 86vh; border-radius: 14px; }
        }
      `}</style>
    </div>
  );
};

export default DsaDrillModal;
